import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CheckoutContext } from '../../../../context/CheckoutContext';
import { CheckoutContextProps } from '../../../../context/CheckoutContext';
import { Service } from '../../../../hooks/useCompany';
import { serviceMatchesAgeGroup } from '../../../../Utils/schoolAgeGroups';

export const useServiceSelector = (
  type: string,
  subType?: string,
  serviceId?: string,
  branchId?: string,
  resourceId?: string,
  sportId?: string,
  uponSelectCallback?: (value: string) => void,
  isPrivate?: boolean,
  ageGroup?: string
) => {
  const [isInitialSelection, setIsInitialSelection] = useState(true);

  const {
    branchesMap,
    servicesMap,
    selectedBranch,
    resourcesMap,
    selectedService,
    selectedResource,
    setSelectedBranch,
    setSelectedService,
    setSelectedResource,
    company,
    selectedSport,
    setSelectedSport,
  } = useContext(CheckoutContext) as CheckoutContextProps;

  const sportsOptions = useMemo(() => {
    if (!company?.sports) return [];
    return [
      {
        value: '',
        label: 'All Sports',
        image: '',
      },
      ...company.sports.map(sport => ({
        value: sport.id,
        label: sport.name,
        image: sport.image,
      })),
    ];
  }, [company?.sports]);

  const branchOptions = useMemo(() => {
    if (!branchesMap || branchesMap.size === 0) return [];
    return Array.from(branchesMap.values())
      .filter(branch => {
        return Array.from(servicesMap?.values() || []).some(service => {
          const matchesType = shouldConsiderThisService(service, type, subType, isPrivate);
          const matchesSport = !selectedSport || service.sportId === selectedSport;
          const matchesAgeGroup = serviceMatchesAgeGroup(service.name, ageGroup);
          return service.branchId === branch.id && matchesType && matchesSport && matchesAgeGroup;
        });
      })
      .map(branch => ({
        value: branch.id,
        label: branch.name,
        isNew: Boolean(branch.isNew),
      }));
  }, [branchesMap, servicesMap, type, subType, selectedSport, isPrivate, ageGroup]);

  const servicesOptions = useMemo(() => {
    if (!servicesMap || servicesMap.size === 0) return [];

    return Array.from(servicesMap.values())
      .filter(service => {
        const matchesType = shouldConsiderThisService(service, type, subType, isPrivate);
        const matchesSport = !selectedSport || service.sportId === selectedSport;
        const matchesAgeGroup = serviceMatchesAgeGroup(service.name, ageGroup);
        return matchesType && matchesSport && matchesAgeGroup;
      })
      .filter(service => service.branchId === selectedBranch)
      .map(service => ({
        value: service.id,
        label: service.name,
        isNew: Boolean(service.isNew),
      }))
      .sort((a, b) => a.label?.localeCompare(b.label));
  }, [servicesMap, type, subType, selectedBranch, selectedSport, isPrivate, ageGroup]);

  const resourceOptions = useMemo(() => {
    const options: any[] = [
      {
        value: '',
        label: 'All Courts',
      },
    ];
    if (!resourcesMap || resourcesMap.size === 0) return options;

    const selectedServiceData = servicesMap?.get(selectedService);

    selectedServiceData?.resources.forEach(resource => {
      options.push({
        value: resource.id,
        label: resource.name,
      });
    });

    options.sort((a, b) => {
      if (a.value === '') return -1;
      if (b.value === '') return 1;
      return a.label?.localeCompare(b.label);
    });

    return options;
  }, [resourcesMap, selectedService, servicesMap, type, subType, selectedBranch, selectedSport]);

  useEffect(() => {
    if (sportsOptions.length === 0) {
      setSelectedSport('');
      return;
    }

    let newSelectedSport = '';

    if (sportId && isInitialSelection && company?.sports?.some(sport => sport.id === sportId)) {
      newSelectedSport = sportId;
    }

    if (newSelectedSport) {
      setSelectedSport(newSelectedSport);
    }
  }, [sportId, company?.sports, sportsOptions, setSelectedSport]);

  useEffect(() => {
    if (branchOptions.length === 0) {
      setSelectedBranch('');
      return;
    }

    let newSelectedLocation = branchOptions[0]?.value;

    if (
      branchId &&
      isInitialSelection &&
      branchOptions.some(option => option.value === branchId)
    ) {
      newSelectedLocation = branchId;
    }

    if (
      serviceId &&
      isInitialSelection &&
      servicesMap?.has(serviceId) &&
      serviceMatchesAgeGroup(servicesMap.get(serviceId)?.name, ageGroup)
    ) {
      const service = servicesMap.get(serviceId)!;
      newSelectedLocation = service?.branchId ?? '';
    }

    if (newSelectedLocation) {
      setSelectedBranch(newSelectedLocation);
    }
  }, [branchId, serviceId, branchOptions, setSelectedBranch, servicesMap, branchesMap, ageGroup]);

  useEffect(() => {
    if (servicesOptions.length === 0) {
      setSelectedService('');
      return;
    }

    let newSelectedService = servicesOptions[0]?.value;

    if (
      serviceId &&
      isInitialSelection &&
      servicesOptions.some(option => option.value === serviceId)
    ) {
      newSelectedService = serviceId;
    }

    if (newSelectedService) {
      setSelectedService(newSelectedService);
    }
  }, [serviceId, servicesMap, servicesOptions, setSelectedService]);

  useEffect(() => {
    if (resourceOptions.length === 0) {
      setSelectedResource('');
      return;
    }

    let newSelectedResource = resourceOptions[0]?.value;

    if (resourceId && resourcesMap?.has(resourceId) && isInitialSelection) {
      newSelectedResource = resourceId;
    }

    setSelectedResource(newSelectedResource);
  }, [resourceId, resourcesMap, resourceOptions, setSelectedResource]);

  const onServiceChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string) => {
      setIsInitialSelection(false);
      setSelectedService(value);
      uponSelectCallback?.(value);
    },
    [setSelectedService, uponSelectCallback, setIsInitialSelection]
  );

  const OnBranchChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string) => {
      setIsInitialSelection(false);
      setSelectedBranch(value);
    },
    [setIsInitialSelection, setSelectedBranch]
  );

  const onResourceChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string) => {
      setIsInitialSelection(false);
      setSelectedResource(value);
    },
    [setIsInitialSelection, setSelectedResource]
  );

  const onSportChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: string) => {
      setIsInitialSelection(false);
      setSelectedSport(value);
    },
    [
      setIsInitialSelection,
      setSelectedSport,
      setSelectedBranch,
      setSelectedService,
      setSelectedResource,
    ]
  );

  const service = servicesMap?.get(selectedService);

  return {
    company,
    selectedBranch,
    selectedService,
    selectedResource,
    selectedSport,
    branchOptions,
    servicesOptions,
    resourceOptions,
    sportsOptions,
    onServiceChange,
    OnBranchChange,
    onResourceChange,
    onSportChange,
    showResourcesSelector: service?.showResourcesSelector,
    selectedServiceData: service,
  };
};

export const shouldConsiderThisService = (service: Service, type: string, subType?: string, isPrivate?: boolean): boolean => {
  if (!service.type) return false;
  else if (!isPrivate && service.isPrivate) return false;
  else if (isPrivate && !service.isPrivate) return false;
  else if (type && subType) return service.type === type && service.subType === subType;
  else if (type) return service.type === type && !service.subType;
  return false;
};
